import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { shoppingListApi } from "@/services/shoppingListApi.ts";
import React, { useState } from "react";
import { Plus, Search, ShoppingCart } from "lucide-react";
import { ItemToDisplay } from "@/pages/ShoppingList/components/ItemToDisplay.tsx";
import { ShoppingItemCreate } from "@/pages/ShoppingList/types.ts";

interface Item {
  _id: string;
  name: string;
  category: string;
  checked?: boolean;
}

export const ShoppingListFocusPage = ({ listId }: { listId: string }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [newItem, setNewItem] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  // Récupération des données de la liste
  const { data, isError, isLoading } = useQuery({
    queryKey: ["shopping-list", listId],
    queryFn: () => shoppingListApi.getListById(listId),
    enabled: !!listId,
  });

  // Mutation pour ajouter un item
  const addItemMutation = useMutation({
    mutationFn: (newItem: ShoppingItemCreate) =>
      shoppingListApi.addItem(listId, newItem),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list", listId] });
      setNewItem("");
      setCategoryInput("");
      setNewCategory("");
    },
  });

  // Mutation pour supprimer un item
  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => shoppingListApi.deleteItem(listId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list", listId] });
    },
  });

  // Mutation pour mettre à jour un item
  const updateItemMutation = useMutation({
    mutationFn: (params: { itemId: string; item: Partial<Item> }) =>
      shoppingListApi.updateItem(listId, params.itemId, params.item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list", listId] });
    },
  });

  // Filtre des items selon le terme de recherche
  const filteredItems =
    data?.items?.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  // Obtenir toutes les catégories uniques
  const getCategories = (): string[] => {
    const categories =
      data?.items?.reduce((acc: string[], item) => {
        if (!acc.includes(item.category)) {
          acc.push(item.category);
        }
        return acc;
      }, []) || [];
    return categories.sort();
  };

  // Regrouper les items par catégorie
  const groupItemsByCategory = () => {
    const result: Record<string, Item[]> = {};

    filteredItems.forEach((item) => {
      if (!result[item.category]) {
        result[item.category] = [];
      }
      result[item.category].push(item);
    });

    return result;
  };

  // Gestion du formulaire d'ajout
  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newItem.trim()) return;

    // Utiliser la catégorie sélectionnée ou celle saisie dans l'input
    const categoryToUse = newCategory || categoryInput || "Non classé";

    const itemToAdd = {
      name: newItem.trim(),
      category: categoryToUse.trim(),
      checked: false,
    };

    addItemMutation.mutate(itemToAdd);
  };

  // Filtrer les suggestions de catégories
  const filteredCategories = getCategories().filter((cat) =>
    cat.toLowerCase().includes(categoryInput.toLowerCase())
  );

  // Sélectionner une catégorie depuis les suggestions
  const selectCategory = (category: string) => {
    setNewCategory(category);
    setCategoryInput(category);
    setShowCategorySuggestions(false);
  };

  // Gestion de la case à cocher
  const handleToggleChecked = (itemId: string, checked: boolean) => {
    updateItemMutation.mutate({
      itemId,
      item: { checked },
    });
  };

  // Gestion de la modification
  const handleEditItem = (item: Item) => {
    // Dans une application réelle, vous ouvririez probablement un modal ici
    const newName = prompt("Modifier l'article", item.name);
    if (newName && newName !== item.name) {
      updateItemMutation.mutate({
        itemId: item._id,
        item: { name: newName },
      });
    }
  };

  // Gestion de la suppression
  const handleDeleteItem = (itemId: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cet article ?")) {
      deleteItemMutation.mutate(itemId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="loading loading-spinner loading-lg text-primary"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-error">
        Erreur lors de la récupération de la liste
      </div>
    );
  }

  const categoriesWithItems = groupItemsByCategory();
  const categories = getCategories();

  return (
    <div className="p-4">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="text-primary" />
            {data?.name}
          </h2>
          <div className="text-sm text-gray-500">
            {filteredItems.length} articles dans {categories.length} catégories
          </div>
        </div>
        <div className="badge badge-lg badge-outline">{data?.type}</div>
      </div>

      {/* Formulaire d'ajout */}
      <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-4 mb-6">
        <form
          onSubmit={handleAddItem}
          className="flex flex-col sm:flex-row gap-2"
        >
          <div className="form-control flex-1">
            <input
              type="text"
              placeholder="Nouvel article"
              className="input input-bordered w-full"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              required
            />
          </div>

          <div className="form-control relative flex-1">
            <input
              type="text"
              placeholder="Catégorie (nouvelle ou existante)"
              className="input input-bordered w-full"
              value={categoryInput}
              onChange={(e) => {
                setCategoryInput(e.target.value);
                setNewCategory(""); // Reset selected category
                setShowCategorySuggestions(true);
              }}
              onFocus={() => setShowCategorySuggestions(true)}
              onBlur={() => {
                // Delay closing suggestions to allow for click
                setTimeout(() => setShowCategorySuggestions(false), 200);
              }}
            />

            {/* Suggestions de catégories */}
            {showCategorySuggestions && categoryInput.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-base-100 border border-base-300 rounded-md shadow-md max-h-44 overflow-y-auto">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat}
                      className="px-3 py-2 hover:bg-base-200 cursor-pointer"
                      onClick={() => selectCategory(cat)}
                    >
                      {cat}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500">
                    "{categoryInput}" sera créée comme nouvelle catégorie
                  </div>
                )}
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary whitespace-nowrap">
            <Plus size={16} />
            Ajouter
          </button>
        </form>
      </div>

      {/* Barre de recherche */}
      <div className="form-control mb-6">
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Rechercher un article..."
            className="input input-bordered w-full pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Affichage des articles par catégorie en colonnes */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchTerm
            ? "Aucun article ne correspond à votre recherche"
            : "Aucun article n'a été ajouté à cette liste"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(categoriesWithItems).map(([category, items]) => (
            <div
              key={category}
              className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-3"
            >
              <ItemToDisplay
                categoryName={category}
                items={items}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
                onToggleChecked={handleToggleChecked}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
